import type { Location } from 'react-router-dom';
import { TENANT_PATHS } from './paths.ts';

/** Location state key for process edit / flowchart back navigation. */
export const PROCESS_RETURN_PATH_STATE_KEY = 'processReturnPath';

/** Allowed prefixes for `processReturnPath` from router location state. */
const ALLOWED_RETURN_PATH_PREFIXES = [
  TENANT_PATHS.SOX_DASHBOARD,
  TENANT_PATHS.SOX_RISKS,
  '/sox-cycles/',
] as const;

export {
  soxProcessesListPath,
  soxProcessCreatePath,
  soxProcessFlowchartPath,
  soxProcessEditPath,
  soxCycleEndToEndFlowchartPath,
  soxCycleCombinedFlowchartPath,
} from './paths.ts';

export function currentPathWithSearch(location: Pick<Location, 'pathname' | 'search'>): string {
  return `${location.pathname}${location.search || ''}`;
}

export function isAllowedProcessReturnPath(path: string): boolean {
  const normalized = String(path ?? '').trim();
  if (!normalized.startsWith('/') || normalized.includes('://')) return false;
  return ALLOWED_RETURN_PATH_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

export function buildProcessReturnState(returnPath: string): { processReturnPath: string } {
  return { [PROCESS_RETURN_PATH_STATE_KEY]: returnPath };
}

export function mergeProcessNavigationState(
  base: Record<string, unknown> | null | undefined,
  returnPath: string
): Record<string, unknown> {
  return { ...(base || {}), [PROCESS_RETURN_PATH_STATE_KEY]: returnPath };
}

export function readProcessReturnPath(state: unknown): string {
  if (!state || typeof state !== 'object') return '';
  const path = (state as Record<string, unknown>)[PROCESS_RETURN_PATH_STATE_KEY];
  const normalized = String(path ?? '').trim();
  if (!normalized.startsWith('/') || !isAllowedProcessReturnPath(normalized)) return '';
  return normalized;
}

export function resolveProcessReturnPath(
  location: Pick<Location, 'state'>,
  fallback: string
): string {
  return readProcessReturnPath(location.state) || fallback;
}
